import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Music, Flame } from 'lucide-react';
import confetti from 'canvas-confetti';
import useSound from 'use-sound';
import { usePitchDetection } from '../../hooks/usePitchDetection';
import { useMidiPlayer } from '../../hooks/useMidiPlayer';
import { pitchTrainingService } from '../../services/pitchTrainingService';
import PracticeSetup from './components/PracticeSetup';
import PracticeSession from './components/PracticeSession';
import ScoringDisplay from './components/ScoringDisplay';

/**
 * Student Pitch Coach Tool
 * Real-time pitch training with MIDI playback, visual feedback, and micro-interactions
 */
export default function StudentPitchCoachTool() {
  const navigate = useNavigate();
  
  // State management
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [mode, setMode] = useState('mix'); // legit, mix, belt
  const [settings, setSettings] = useState({
    pitchTolerance: 20,
    timingWindow: 120,
    confidenceThreshold: 0.85,
    vibratoSmoothing: 200,
    playReference: true,
    metronome: false,
    countIn: true
  });
  const [sessionState, setSessionState] = useState('setup'); // setup, practicing, results
  const [sessionData, setSessionData] = useState(null);
  const [streak, setStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [recentSessions, setRecentSessions] = useState([]);

  // Micro-interaction refs
  const lastStreakRef = useRef(0);
  const celebrationTriggeredRef = useRef(false);

  // Sound effects (using placeholder paths - replace with actual sound files)
  const [playSuccess] = useSound('/sounds/success.mp3', { volume: 0.5 });
  const [playPerfect] = useSound('/sounds/perfect.mp3', { volume: 0.7 });
  const [playStreak] = useSound('/sounds/streak.mp3', { volume: 0.6 });

  // Hooks
  const midiPlayer = useMidiPlayer();
  const pitchDetection = usePitchDetection({
    smoothingWindow: settings?.vibratoSmoothing,
    confidenceThreshold: settings?.confidenceThreshold,
    onPitchDetected: handlePitchDetected
  });

  // Load recent sessions
  useEffect(() => {
    loadRecentSessions();
  }, []);

  const loadRecentSessions = async () => {
    const { data } = await pitchTrainingService?.getUserSessions(5);
    if (data) {
      setRecentSessions(data);
    }
  };

  // Pitch detection callback
  function handlePitchDetected(pitchData) {
    if (sessionState !== 'practicing' || !midiPlayer?.isPlaying) return;

    const expectedNote = midiPlayer?.getExpectedNoteAt(
      midiPlayer?.currentTime,
      settings?.timingWindow / 1000
    );

    if (!expectedNote) {
      // No expected note (rest)
      setStreak(0);
      return;
    }

    // Calculate cents error
    const centsError = Math.abs(
      pitchDetection?.calculateCents(pitchData?.frequency, expectedNote?.frequency)
    );

    // Check if in tune
    const isInTune = centsError <= settings?.pitchTolerance && pitchData?.confidence >= settings?.confidenceThreshold;

    if (isInTune) {
      // Increment streak
      const newStreak = streak + 1;
      setStreak(newStreak);
      setMaxStreak(Math.max(maxStreak, newStreak));

      // Trigger micro-interactions
      triggerSuccessFeedback(newStreak);
    } else {
      // Reset streak
      if (streak > 0) {
        setStreak(0);
        lastStreakRef.current = streak;
      }
    }
  }

  // Micro-interaction: Success feedback
  const triggerSuccessFeedback = useCallback((currentStreak) => {
    // Milestone celebrations
    if (currentStreak === 5) {
      playStreak?.();
      fireConfetti('streak');
      showStreakNotification(5, '🔥 On Fire!');
    } else if (currentStreak === 10) {
      playPerfect?.();
      fireConfetti('perfect');
      showStreakNotification(10, '⭐ Amazing!');
    } else if (currentStreak === 20) {
      playPerfect?.();
      fireConfetti('epic');
      showStreakNotification(20, '🎯 Unstoppable!');
    } else if (currentStreak % 3 === 0) {
      // Every 3 in a row
      playSuccess?.();
    }
  }, [playStreak, playPerfect, playSuccess]);

  // Confetti animations
  const fireConfetti = (type) => {
    switch (type) {
      case 'streak':
        confetti({
          particleCount: 50,
          spread: 60,
          origin: { y: 0.7 },
          colors: ['#ff6b6b', '#feca57', '#ee5a6f']
        });
        break;
      case 'perfect':
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#1dd1a1', '#10ac84', '#48dbfb']
        });
        break;
      case 'epic':
        // Epic celebration
        const duration = 2000;
        const end = Date.now() + duration;
        const colors = ['#ff6b6b', '#feca57', '#1dd1a1', '#48dbfb', '#ee5a6f'];
        
        (function frame() {
          confetti({
            particleCount: 3,
            angle: 60,
            spread: 55,
            origin: { x: 0 },
            colors: colors
          });
          confetti({
            particleCount: 3,
            angle: 120,
            spread: 55,
            origin: { x: 1 },
            colors: colors
          });

          if (Date.now() < end) {
            requestAnimationFrame(frame);
          }
        }());
        break;
      case 'completion':
        confetti({
          particleCount: 150,
          spread: 180,
          origin: { y: 0.5 },
          colors: ['#ff6b6b', '#feca57', '#1dd1a1', '#48dbfb', '#ee5a6f', '#5f27cd']
        });
        break;
    }
  };

  // Show streak notification
  const showStreakNotification = (streakCount, message) => {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = 'fixed top-20 left-1/2 transform -translate-x-1/2 z-50 animate-bounce';
    notification.innerHTML = `
      <div class="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-full shadow-2xl text-xl font-bold flex items-center gap-3">
        <span class="text-3xl">${message}</span>
        <span class="text-2xl">${streakCount} Streak!</span>
      </div>
    `;
    document.body?.appendChild(notification);

    // Remove after animation
    setTimeout(() => {
      notification?.classList?.add('animate-fadeOut');
      setTimeout(() => document.body?.removeChild(notification), 500);
    }, 2500);
  };

  // Start practice session
  const startPractice = async () => {
    if (!selectedTrack) return;

    // Load MIDI file
    const { data } = await pitchTrainingService?.getTrackById(selectedTrack?.id);
    if (!data) return;

    // Fetch and load MIDI
    const response = await fetch(data?.file_url);
    const blob = await response?.blob();
    const file = new File([blob], data.file_name, { type: 'audio/midi' });
    
    await midiPlayer?.loadMidiFile(file);
    
    // Start listening
    await pitchDetection?.startListening();
    
    // Count-in if enabled
    if (settings?.countIn) {
      // TODO: Implement count-in metronome
    }

    // Start playback
    await midiPlayer?.play();
    
    setSessionState('practicing');
    setStreak(0);
    setMaxStreak(0);
    celebrationTriggeredRef.current = false;
  };

  // Stop practice session
  const stopPractice = async () => {
    midiPlayer?.stop();
    pitchDetection?.stopListening();
    
    // Calculate results
    const results = calculateResults();
    setSessionData(results);
    
    // Save session
    await pitchTrainingService?.createSession({
      melodyTrackId: selectedTrack?.id,
      mode,
      pitchToleranceCents: settings?.pitchTolerance,
      timingWindowMs: settings?.timingWindow,
      vibratoSmoothing: settings?.vibratoSmoothing,
      confidenceThreshold: settings?.confidenceThreshold,
      durationSeconds: midiPlayer?.currentTime,
      pitchAccuracyPercent: results?.pitchAccuracy,
      meanCentsError: results?.meanCentsError,
      noteHitRate: results?.noteHitRate,
      longestStreak: maxStreak,
      grade: results?.grade,
      directorNote: results?.directorNote
    });

    // Completion celebration
    if (results?.pitchAccuracy >= 90) {
      fireConfetti('completion');
      playPerfect?.();
    }

    setSessionState('results');
    await loadRecentSessions();
  };

  // Calculate session results
  const calculateResults = () => {
    // TODO: Implement actual calculation based on collected data during session
    // For now, return placeholder
    const accuracy = 85;
    const grade = accuracy >= 90 ? 'A' : accuracy >= 80 ? 'B' : accuracy >= 70 ? 'C' : accuracy >= 60 ? 'D' : 'F';
    const directorNote = 
      grade === 'A' ? 'Standing ovation energy! 👏' :
      grade === 'B' ? 'Strong! Clean up a few spots and you\'re stage-ready.' :
      grade === 'C' ? 'Good foundation—aim for steadier centers on long notes.' :
      grade === 'D' ? 'You\'ve got it—slow it down and lock the target notes.' :
      'No stress—let\'s start with smaller chunks and build confidence.';

    return {
      pitchAccuracy: accuracy,
      meanCentsError: 12,
      noteHitRate: 0.8,
      longestStreak: maxStreak,
      grade,
      directorNote
    };
  };

  // Reset to setup
  const resetToSetup = () => {
    setSessionState('setup');
    setSessionData(null);
    setStreak(0);
    setMaxStreak(0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d] py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-6 border border-white/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-500/30 rounded-xl">
                <Music className="w-8 h-8 text-purple-200" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">
                  🎤 Pitch Coach
                </h1>
                <p className="text-white/80">Level up your pitch game!</p>
              </div>
            </div>

            {/* Streak Display */}
            {sessionState === 'practicing' && (
              <div className="flex items-center gap-4">
                <div className="bg-gradient-to-r from-orange-400 to-red-500 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-2 animate-pulse">
                  <Flame className="w-6 h-6" />
                  <span className="text-xl font-bold">{streak} Streak</span>
                </div>
                {maxStreak > 0 && (
                  <div className="bg-white/10 px-4 py-2 rounded-full border border-white/20">
                    <span className="text-sm text-white/80">Best: {maxStreak}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        {sessionState === 'setup' && (
          <PracticeSetup
            selectedTrack={selectedTrack}
            setSelectedTrack={setSelectedTrack}
            mode={mode}
            setMode={setMode}
            settings={settings}
            setSettings={setSettings}
            onStartPractice={startPractice}
            recentSessions={recentSessions}
          />
        )}

        {sessionState === 'practicing' && (
          <PracticeSession
            midiPlayer={midiPlayer}
            pitchDetection={pitchDetection}
            settings={settings}
            streak={streak}
            onStop={stopPractice}
          />
        )}

        {sessionState === 'results' && sessionData && (
          <ScoringDisplay
            sessionData={sessionData}
            selectedTrack={selectedTrack}
            onRestart={resetToSetup}
          />
        )}
      </div>
    </div>
  );
}